import React, { useCallback, useState } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import {
  Collapse,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Navbar,
  Nav,
  Container,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { Link, Modal } from "stories/components";
import { useAuthContext } from "contexts/AuthContext";
import defaultAvatar from "assets/images/boss-1.png";

import NewEvent from "views/auth/NewEvent";

import "./AdminNavbar.css";

function getInitials(user) {
  const fn = (user?.first_name || "").trim();
  const ln = (user?.last_name || "").trim();
  if (fn && ln) return (fn[0] + ln[0]).toUpperCase();
  if (fn.length >= 2) return fn.slice(0, 2).toUpperCase();
  if (fn.length === 1) return fn.toUpperCase();
  const email = (user?.email || "").trim();
  if (email.length >= 2) return email.slice(0, 2).toUpperCase();
  return "?";
}

function AdminNavbar({
  myUser,
  theme,
  sidenavOpen,
  topNavOpen,
  toggleTopNav,
  topNavButtons,
  isOpen,
  setIsOpen,
  isMobile,
  setMobileMenuOpen,
  style,
}) {
  const { t } = useTranslation();
  const { logout, accountUserType } = useAuthContext();

  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [modalEditId, setModalEditId] = useState();

  const toggleModalEdit = () => {
    setModalEditOpen((s) => !s);
  };

  const modalEditResolve = () => {
    setModalEditOpen(false);
    setModalEditId(null);
  };

  const modalEditReject = () => {
    setModalEditOpen(false);
    setModalEditId(null);
  };

  const handleLogout = () => {
    logout();
  };

  const handleSidenavToggle = () => {
    if (isMobile && setMobileMenuOpen) {
      setMobileMenuOpen((open) => !open);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const roleLabel = () => {
    if (accountUserType === "admin") {
      return t("navbar_role_admin", "Pääkäyttäjä");
    }
    if (accountUserType === "read_only") {
      return t("navbar_role_readonly", "Lukuoikeus");
    }
    if (accountUserType === "basic") {
      return t("navbar_role_basic", "Käyttäjä");
    }
    return t("navbar_role_user", "Käyttäjä");
  };

  const displayName =
    myUser?.first_name ||
    (myUser?.email ? myUser.email.split("@")[0] : "") ||
    myUser?.email ||
    "";

  const renderTopNavButtons = useCallback(() => {
    if (!topNavButtons?.length) return null;
    return (
      <div className="sk-topbar__api-buttons d-flex align-items-center">
        {topNavButtons.map((topNavButton, index) => (
          <div className="mr-2" key={index}>
            <Link {...topNavButton}>
              <span className="btn btn-sm btn-outline-secondary sk-topbar__api-btn">
                {topNavButton?.name}{" "}
                <span className="avatar rounded-circle bg-primary sk-topbar__api-btn-icon">
                  <i className={`fas ${topNavButton?.icon_font_awesome}`} />
                </span>
              </span>
            </Link>
          </div>
        ))}
      </div>
    );
  }, [topNavButtons]);

  const hasPhoto = Boolean(myUser?.profile_picture);

  return (
    <>
      <Navbar
        expand
        className={classnames(
          "navbar-top sk-topbar border-bottom",
          { "navbar-dark": theme === "dark" },
          { "navbar-light bg-white": theme === "light" },
          { "d-none": topNavOpen === false },
        )}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1030,
          ...(style || {}),
        }}
      >
        <Container fluid className="sk-topbar__container">
          <div className="sk-topbar__row d-flex align-items-center w-100">
            <button
              type="button"
              className="sk-topbar__sidenav-toggle"
              onClick={handleSidenavToggle}
              aria-label={t("navbar_toggle_menu", "Näytä tai piilota valikko")}
              title={t("navbar_toggle_menu", "Näytä tai piilota valikko")}
            >
              <i className="ni ni-align-left-2 sk-topbar__sidenav-toggle-icon" aria-hidden />
            </button>

            {renderTopNavButtons()}

            <Collapse
              navbar
              isOpen={true}
              className="sk-topbar__nav-collapse ml-auto flex-grow-0"
            >
              <Nav
                className="sk-topbar__nav-right align-items-center flex-nowrap"
                navbar
              >
                <UncontrolledDropdown nav inNavbar tag="li" className="sk-topbar__user-nav-item flex-shrink-0 nav-item">
                  <DropdownToggle
                    tag="button"
                    type="button"
                    className="sk-topbar__user-toggle btn btn-link"
                  >
                      <span className="sk-topbar__user-pill">
                        {hasPhoto ? (
                          <span className="sk-topbar__avatar sk-topbar__avatar--img">
                            <img
                              alt=""
                              src={myUser.profile_picture || defaultAvatar}
                            />
                          </span>
                        ) : (
                          <span
                            className="sk-topbar__avatar sk-topbar__avatar--initials"
                            aria-hidden
                          >
                            {getInitials(myUser)}
                          </span>
                        )}
                        <span className="sk-topbar__user-text d-flex flex-column text-left">
                          <span className="sk-topbar__user-name">
                            {displayName}
                          </span>
                          <span className="sk-topbar__user-role">
                            {roleLabel()}
                          </span>
                        </span>
                        <i
                          className="ni ni-bold-down sk-topbar__user-chevron"
                          aria-hidden
                        />
                      </span>
                    </DropdownToggle>
                    <DropdownMenu right className="sk-topbar__dropdown">
                      <DropdownItem
                        className="sk-topbar__dropdown-header font-weight-bold text-dark"
                        header
                        tag="div"
                      >
                        {t("navbar_account_menu_title", "Tilini")}
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem
                        tag={RouterLink}
                        to="/dashboard/other/user_account"
                        className="sk-topbar__dropdown-item"
                      >
                        <i className="ni ni-single-02 sk-topbar__dropdown-icon" />
                        <span>{t("navbar_my_account", "Oma tili")}</span>
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem
                        className="sk-topbar__dropdown-logout"
                        onClick={handleLogout}
                      >
                        <i className="ni ni-button-power sk-topbar__dropdown-icon" />
                        <span>{t("sidebar_logout", "Kirjaudu ulos")}</span>
                      </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
              </Nav>
            </Collapse>
          </div>
        </Container>
        <Modal
          showHeader={false}
          isOpen={modalEditOpen}
          toggleModal={toggleModalEdit}
          classNameBody="p-0 padding-0"
        >
          <NewEvent
            objectId={modalEditId}
            handleResolve={modalEditResolve}
            handleReject={modalEditReject}
          />
        </Modal>
      </Navbar>
    </>
  );
}

AdminNavbar.defaultProps = {
  sidenavOpen: false,
  theme: "light",
  isMobile: false,
  setMobileMenuOpen: undefined,
};
AdminNavbar.propTypes = {
  sidenavOpen: PropTypes.bool,
  theme: PropTypes.oneOf(["dark", "light"]),
  isMobile: PropTypes.bool,
  setMobileMenuOpen: PropTypes.func,
};

export default AdminNavbar;
